---
title: "Your free allowance, and what happens when it runs out"
description: "The free plan includes 100 call recordings, 100 WhatsApp messages and 300 outreach emails. Here's how the allowance works and what changes when you upgrade."
publishedAt: "2026-08-14"
updatedAt: "2026-10-24"
slug: "free-allowance-and-upgrading"
---

# Your free allowance, and what happens when it runs out

Most of NerdCRM is free for up to 5 users. Three features are paid, and each one comes with a free allowance so you can try it properly before you decide:

| Feature | Free allowance |
|---|---|
| Call recording | 100 recordings |
| WhatsApp | 100 outbound messages |
| Cold email | 300 outreach emails |

The allowance is **one-time per organization**, not monthly. It's there so you can see the feature work on your own data before paying — not as a recurring free tier.

## What counts against it

Only things that actually leave the building, and only while you're on the free plan:

- **Call recording** — one unit per recording stored. Re-uploading a recording we already have costs nothing.
- **WhatsApp** — one unit per outbound message, whether it comes from a campaign or from a reply you send in the **Inbox** (free text inside the 24-hour window, or a template after it closes). Messages your contacts send *you* are free.
- **Cold email** — one unit per recipient. Campaign sends and follow-ups count. **Replies you send from the Inbox do not** — once a prospect writes to you, continuing that conversation is free.

Ordinary CRM email — password resets, activity reminders, KAM report digests — is never counted.

## What happens at the limit

The feature pauses. Nothing is deleted, and nothing is lost:

- Your existing recordings, conversations and campaign history stay exactly where they are, and everything already in your **Inbox** stays readable.
- A running campaign **pauses** rather than failing. Contacts already enrolled keep their place in the sequence — nobody gets skipped and nobody gets a duplicate.
- Your reps' recorders (the uploader next to MicroSIP on Windows, or Nerd Dial on a Mac) keep queueing recordings on their machines and upload them once you upgrade, so calls made during the pause aren't lost either.

You'll see it in the app:

- **Recordings** shows a lock in the sidebar. Its page says how much of the allowance you've used, with an upgrade button for the owner.
- On **Campaigns**, picking the used-up channel in the **New campaign** menu (or **New campaign** itself, if only one channel is on) opens an explanation instead of the builder, and the menu marks that channel with a lock. **Campaigns** itself gets a lock in the sidebar once every channel you can use is used up.
- Only the organization owner gets an **Upgrade** button. Everyone else is told to ask their owner.

## We warn you first

At 80% of an allowance we send the organization owner an email and an in-app notification, and admins get the in-app notification too. You get a second notice when an allowance runs out. We only send each notice once — a paused campaign retrying in the background will never spam you.

If you're about to launch a campaign bigger than what's left, the **Review & launch** step tells you before it starts: *"you have 20 free outreach emails left, so this campaign will pause after 20 of 500."*

## Upgrading

Upgrading is $4 per user per month and unlocks all three features with no per-feature limits. Only the organization **owner** can open **Settings → Billing & seats** to upgrade — if you're an admin, ask your owner.

After you upgrade:

1. All three features unlock immediately.
2. **Resume any campaign that was paused** — open it from **Campaigns** and click **Resume**. It won't restart itself, so you stay in control of when sending picks up again.
3. Queued recordings on your reps' machines upload on their own within a few minutes.

See [how billing and users work](/help/how-billing-and-users-work) for seats, the AI add-on and cancelling.

## If you cancel later

Your allowance doesn't reset. It's a one-time trial, so cancelling and resubscribing won't produce a second free 100 — the features simply return to their paused state, with all of your data intact.
